import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { format } from "date-fns";
import { ListChecks, X } from "lucide-react";
import { Button } from "components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "components/ui/dialog";
import { useUserInfo } from "../../context/user";
import { useTaskDrafts, useDeleteTaskDraft } from "../../queries/taskDrafts";
import { UNFINISHED_TASKS_KEY } from "../../lib/taskDrafts";



const NEW_TASK_PATH = "/app/chat/tasks/new";
const NEW_TASK_LANDING = "/app/chat";

const formatWhen = (ts) => {
  const d = new Date(ts);
  return isNaN(d.getTime()) ? "" : format(d, "dd/MM/yyyy · HH:mm");
};

const MAX_CARD_TEXT = 70;
const cardText = (title, prompt, max = MAX_CARD_TEXT) => {
  const clean = (title || prompt || "").trim();
  return clean.length > max ? `${clean.slice(0, max).trim()}…` : clean;
};

const Drafts = () => {
  const navigate = useNavigate();
  const [, , , , userActiveLocation] = useUserInfo();
  const locationId = userActiveLocation?.id;

  const { data: drafts = [] } = useTaskDrafts(locationId);
  const { mutate: deleteTaskDraft } = useDeleteTaskDraft();
  const [pendingDelete, setPendingDelete] = useState(null);

  const groups = drafts.reduce((acc, d) => {
    const found = acc.find((g) => g.key === d.parentKey);
    if (found) found.items.push(d);
    else acc.push({ key: d.parentKey, title: d.parentTitle, items: [d] });
    return acc;
  }, []);

  const openDraft = (draft) => {
    const isUnfinished = draft.parentKey === UNFINISHED_TASKS_KEY;
    if (!isUnfinished) {
      deleteTaskDraft({ id: draft._id, locationId });
    }
    const state = isUnfinished
      ? {
          resumeTaskId: draft._id,
          resumeTaskTitle: draft.title,
          resumeTaskContent: draft.prompt,
        }
      : { autoloadfirsttext: draft.prompt };
    navigate(NEW_TASK_PATH, { state });
  };

  const confirmDiscard = () => {
    if (pendingDelete) deleteTaskDraft({ id: pendingDelete._id, locationId });
    setPendingDelete(null);
  };

  return (
    <div className="w-full h-full flex justify-center overflow-hidden">
      <div className="newUI_container v3chat__input !h-full overflow-y-auto">
        <header className="mb-7">
          <span className="inline-flex items-center gap-1.5 text-xs font-medium uppercase tracking-wide text-muted-foreground">
            <ListChecks className="h-3.5 w-3.5" />
            Drafts
          </span>
          <div className="page-header">
            <h1 className="page-display text-3xl md:text-4xl">
              Pick up where you <span className="accent">left off</span>
            </h1>
            <p className="page-lede">
              {drafts.length === 0
                ? "If you start building a Task and leave before saving it, it'll be saved here so you can come back to it later."
                : "Tasks you started building but haven't saved yet. Open one to keep going, or clear what you no longer need."}
            </p>
          </div>
        </header>

        {drafts.length === 0 ? (
          <div className="mt-2">
            <button
              type="button"
              onClick={() => navigate(NEW_TASK_LANDING)}
              className="inline-flex items-center gap-1.5 h-9 px-4 rounded-md border border-foreground bg-foreground text-sm font-medium text-background transition-colors hover:bg-background hover:text-foreground"
            >
              Build a new Task →
            </button>
          </div>
        ) : (
          <div className="space-y-10">
            {groups.map((group) => (
              <section key={group.key}>
                <div className="mb-3">
                  <h2
                    className="text-base font-medium text-foreground tracking-tight"
                    style={{ fontFamily: "'JetBrains Mono', ui-monospace, SFMono-Regular, Menlo, monospace" }}
                  >
                    Unfinished Tasks
                    <span className="ml-2 text-xs font-light text-muted-foreground">
                      {group.items.length} draft
                      {group.items.length === 1 ? "" : "s"}
                    </span>
                  </h2>
                </div>
                <ul className="grid gap-3 sm:grid-cols-2">
                  {group.items.map((draft, i) => (
                    <li key={draft._id} className="group/draft relative">
                      <button
                        type="button"
                        onClick={() => openDraft(draft)}
                        className="interactive-surface flex h-full w-full flex-col items-start gap-3 rounded-2xl bg-card p-5 text-left"
                      >
                        <div className="flex w-full items-center gap-3">
                          <span
                            className="text-[10px] font-medium tracking-[0.18em] text-muted-foreground/70"
                            style={{ fontFamily: "'JetBrains Mono', ui-monospace, monospace" }}
                          >
                            {String(i + 1).padStart(2, "0")}
                          </span>
                          <span className="text-[11px] font-light text-muted-foreground/70">
                            Captured {formatWhen(draft.createdAt)}
                          </span>
                        </div>
                        <p className="text-sm font-light leading-relaxed text-foreground line-clamp-2">
                          {cardText(draft.title, draft.prompt)}
                        </p>
                        <span className="mt-auto text-xs font-medium text-[hsl(var(--task-accent))]">
                          Open in Automate →
                        </span>
                      </button>
                      <button
                        type="button"
                        aria-label="Discard draft"
                        onClick={() => setPendingDelete(draft)}
                        className="absolute right-3 top-3 hidden group-hover/draft:inline-flex p-1 text-muted-foreground hover:text-foreground"
                      >
                        <X className="h-3.5 w-3.5" />
                      </button>
                    </li>
                  ))}
                </ul>
              </section>
            ))}
          </div>
        )}
      </div>

      <Dialog
        open={!!pendingDelete}
        onOpenChange={(open) => !open && setPendingDelete(null)}
      >
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Discard this draft?</DialogTitle>
            <DialogDescription>
              This draft Task will be removed for good. You can always
              describe it again from scratch.
            </DialogDescription>
          </DialogHeader>
          {pendingDelete && (
            <p className="rounded-lg border border-border/60 bg-muted/40 p-3 text-sm font-light leading-relaxed text-foreground line-clamp-3">
              {cardText(pendingDelete.title, pendingDelete.prompt, 160)}
            </p>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setPendingDelete(null)}>
              Keep draft
            </Button>
            <Button onClick={confirmDiscard}>Discard draft</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default Drafts;
